import { Link, useParams } from "react-router-dom";
import { Check, MessageSquare, ArrowUpRight } from "lucide-react";
import { useSite, money } from "@/lib/site";
import { Reveal, iconFor } from "@/components/site/common";
import { STEPS, CtaBand } from "@/components/site/Sections";
import { BuyButton } from "@/components/site/Pricing";
import NotFound from "./NotFound";
import { ReviewQuote } from "@/components/site/Reviews";

const PriceBox = ({ p }) => (
  <div className="rounded-3xl bg-[#090D16] text-white p-7 sm:p-8 grain relative overflow-hidden" data-testid="service-price-box">
    <p className="overline !text-sky-400">Starting price</p>
    <p className="mt-3 font-display text-5xl font-extrabold tracking-tight" data-testid="service-detail-price">{money(p.price)}</p>
    <p className="text-sm text-slate-400 mt-1">USD · one-time payment</p>
    {p.note && <p className="mt-4 text-xs text-slate-400">{p.note}</p>}
    <div className="mt-7 flex flex-col gap-2.5 relative">
      <BuyButton pkg={p} className="w-full py-4" />
      <Link to={`/contact?service=${encodeURIComponent(p.name)}`} data-testid="service-contact-btn"
        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 py-3.5 text-sm font-semibold hover:bg-white/10 transition-colors">
        <MessageSquare className="w-4 h-4" /> Contact about this service
      </Link>
    </div>
  </div>
);

const ServiceHero = ({ p }) => {
  const Icon = iconFor(p.id);
  return (
    <section className="relative overflow-hidden grid-lines border-b">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-16 grid lg:grid-cols-12 gap-10 items-end">
        <Reveal className="lg:col-span-7">
          <Link to="/services" className="overline hover:underline" data-testid="service-back-link">← Services</Link>
          <span className="mt-6 grid place-items-center w-14 h-14 rounded-2xl bg-blue-600 text-white"><Icon className="w-6 h-6" /></span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900" data-testid="service-detail-title">{p.name}</h1>
          <p className="mt-4 text-lg text-slate-600">{p.tagline}</p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="rounded-3xl overflow-hidden aspect-[4/3] shadow-xl bg-slate-100">
            {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const OtherServices = ({ others }) => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-8">
    <p className="overline mb-6">Other services</p>
    <div className="grid sm:grid-cols-3 gap-4">
      {others.map((o) => (
        <Link key={o.id} to={`/services/${o.id}`} data-testid={`service-other-${o.id}`} className="group flex items-center justify-between rounded-2xl bg-white border border-slate-200 p-5 hover:border-blue-500 transition-colors">
          <span className="font-display font-semibold text-slate-900">{o.name}</span>
          <ArrowUpRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors" />
        </Link>
      ))}
    </div>
  </section>
);

export default function ServiceDetail() {
  const { id } = useParams();
  const { packages, loaded } = useSite();
  const p = packages.find((x) => x.id === id);
  if (!loaded) return <div className="min-h-screen" />;
  if (!p) return <NotFound />;
  const others = packages.filter((x) => x.id !== id);
  return (
    <>
      <ServiceHero p={p} />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-14">
          <Reveal>
            <p className="overline mb-4">Overview</p>
            <p className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-snug text-slate-900" data-testid="service-detail-description">{p.description}</p>
          </Reveal>
          <Reveal>
            <p className="overline mb-6">What's included</p>
            <ul className="grid sm:grid-cols-2 gap-3">
              {p.features.map((f) => (
                <li key={f} className="flex gap-3 rounded-2xl bg-white border border-slate-200 p-4 text-slate-700"><Check className="w-5 h-5 shrink-0 text-emerald-500" />{f}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal>
            <p className="overline mb-6">How it works</p>
            <ol className="space-y-4">
              {STEPS.map(([t, d], i) => (
                <li key={t} className="flex gap-5"><span className="font-mono text-sm text-blue-600 pt-1">0{i + 1}</span><div><p className="font-display font-bold text-slate-900">{t}</p><p className="text-sm text-slate-600">{d}</p></div></li>
              ))}
            </ol>
          </Reveal>
        </div>
        <div className="lg:col-span-5"><div className="lg:sticky lg:top-28"><PriceBox p={p} /><ReviewQuote serviceId={p.id} /></div></div>
      </section>
      {others.length > 0 && <OtherServices others={others} />}
      <CtaBand />
    </>
  );
}
